import { compose } from 'redux';
import createActionByConditionFactory from '@Utils/Redux/actionByConditionFactory';

import {
  updateGroupById,
  updateFilterById,
  addFilterGroupsById,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';

import {
  commercialSuppliersFilter as commercialSuppliersFilterSelector,
  paramsForFilterGroups,
  keyifyParamsForFiltersRequest,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import { clearSelectedItemsFromDialog } from '@Containers/ResultsFilters/ResultsFiltersComponents/SelectedItemsFromDialog/SelectedItemsFromDialogActions';
import { getNormalizedFilterGroupsAsync } from '@Containers/ResultsFilters/ResultsFiltersService';
import { config } from '@Constants/rx-ui/constants';
import { preferredSuppliersFilterId } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import { commercialSuppliersFilterFactory } from './BarChartFilterSelectors';

export function toggleFilterUserInput(filter) {
  return (dispatch, getState) => {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);

    const action = updateFilterById(key, filter.get('id'), {
      userInputOpen: !filter.get('userInputOpen'),
    });
    dispatch(action);
  };
}

export function closeFilterUserInput(key, filterId) {
  return (dispatch) => {
    const action = updateFilterById(key, filterId, {
      userInputOpen: false,
    });
    dispatch(action);
  };
}

export function updateFilterUserInputValue(filter, userInputValue) {
  return (dispatch, getState) => {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);
    // Filter update user input value
    Logger.info('Filters: update user input value', { filterId: filter.get('id'), userInputValue });
    const action = updateFilterById(key, filter.get('id'), {
      userInputValue,
    });
    dispatch(action);
  };
}

export function updateSuppliersPreferencesGroupsValues(key, filterId, groupId, values) {
  return (dispatch) => {
    const action = updateGroupById(key, filterId, groupId, {
      values,
    });
    dispatch(action);
  };
}

export function toggleCommercialSuppliersFilterGroup(key, filterId, groupId, selected) {
  return (dispatch) => {
    const action = updateGroupById(key, filterId, groupId, {
      selected,
    });
    dispatch(action);
  };
}

export function toggleFilterGroup(filter, group) {
  return (dispatch, getState) => {
    const filterId = filter.get('id');
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);
    const selected = group.get('selected');

    const action = updateGroupById(key, filterId, group.get('id'), {
      selected: !selected,
    });
    dispatch(action);

    const isPreferredSuppliers = filterId === preferredSuppliersFilterId;
    if (isPreferredSuppliers) {
      const values = group.get('values');

      if (!values) {
        return;
      }

      const { commercialSuppliersFilterGroups } = commercialSuppliersFilterFactory();
      const commercialSuppliersGroups = commercialSuppliersFilterGroups(getState());
      const commercialSuppliersFilter = commercialSuppliersFilterSelector(getState());
      const commercialSuppliersFilterId = commercialSuppliersFilter.get('id');

      // eslint-disable-next-line no-shadow
      const groupsToUpdate = commercialSuppliersGroups.filter((group) => values.find((value) => group.get('id') === value.get('title')));

      dispatch(closeFilterUserInput(key, commercialSuppliersFilterId));
      // eslint-disable-next-line no-shadow
      groupsToUpdate.forEach((group) => {
        dispatch(toggleCommercialSuppliersFilterGroup(key, commercialSuppliersFilterId, group.get('id'), !selected));
      });
    }

    dispatch(clearSelectedItemsFromDialog(filter));
  };
}

function updateFilterPropertyFactory(property, value) {
  return function updateFilterProperty(dispatch, getState, filter) {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);
    const id = filter.get('id');

    dispatch(
      updateFilterById(key, id, {
        [property]: value,
      })
    );
  };
}

const setIsGroupsFetchingTruthy = updateFilterPropertyFactory('isGroupsFetching', true);
const setIsGroupsFetchingFalsy = updateFilterPropertyFactory('isGroupsFetching', false);

export const ifGroupsCurrentlyNotFetching = createActionByConditionFactory({
  shouldPerformAction: (getState, filter) => !filter.get('isGroupsFetching'),
  beforeAction: setIsGroupsFetchingTruthy,
  afterAction: setIsGroupsFetchingFalsy,
  onError: setIsGroupsFetchingFalsy,
});

const setIsGroupsFetchedTruthy = updateFilterPropertyFactory('isGroupsFetched', true);
const setIsGroupsFetchedFalsy = updateFilterPropertyFactory('isGroupsFetched', false);

export const ifGroupsNotFetched = createActionByConditionFactory({
  shouldPerformAction: (getState, filter, fetchAll) => {
    if (!filter.get('isGroupsFetched')) {
      return true;
    }
    // if needed to load all groups then check how many were preloaded before
    const currentGroups = filter.get('groups').count();
    const totalGroups = filter.get('groupCount');
    return fetchAll && currentGroups < totalGroups;
  },
  beforeAction: setIsGroupsFetchedTruthy,
  onError: setIsGroupsFetchedFalsy,
});

export const ifGroupsAreFetched = createActionByConditionFactory({
  shouldPerformAction: (getState, filter) => filter.get('isGroupsFetched'),
});

const makeFetchFilterGroups = (filter, fetchAll) => async (dispatch, getState) => {
  const state = getState();
  const baseParams = paramsForFilterGroups(state);
  const id = filter.get('id');
  const isThesaurus = filter.get('thesaurus');
  const properties = {
    property: id,
    all: !!isThesaurus,
  };
  if (fetchAll && filter.get('groups') && filter.get('groups').count()) {
    // load the rest suppliers if filter already was opened/preselected
    const currentGroups = filter.get('groups');
    properties.first = currentGroups.count() + 1;
    properties.last = filter.get('groupCount');
  } else if (fetchAll) {
    // just load all
    properties.all = true;
  }
  const resultParams = {
    ...baseParams,
    properties: [properties],
  };

  const response = await getNormalizedFilterGroupsAsync(resultParams);

  return response;
};

const ifGroupsNotFetchedAndNotFetching = compose(ifGroupsNotFetched, ifGroupsCurrentlyNotFetching);

export const fetchFilterGroups = ifGroupsNotFetchedAndNotFetching((filter, fetchAll = false) => async (dispatch, getState) => {
  const state = getState();
  const key = keyifyParamsForFiltersRequest(state);
  const id = filter.get('id');

  const response = await dispatch(makeFetchFilterGroups(filter, fetchAll));
  const { groups, hitCount, groupCount, thesaurusRootId, thesaurusGroups, index } = response;
  const action = addFilterGroupsById(key, id, groups, groupCount, thesaurusGroups, hitCount, thesaurusRootId, index);
  dispatch(action);
});

export const updateFilterGroups = ifGroupsAreFetched((filter) => async (dispatch, getState) => {
  const state = getState();
  const key = keyifyParamsForFiltersRequest(state);
  const id = filter.get('id');

  const response = await dispatch(makeFetchFilterGroups(filter));
  const { groups } = response;

  groups.forEach((group) => dispatch(updateSuppliersPreferencesGroupsValues(key, id, group.id, group.values)));
});

export const fetchMoreFilterGroups = ifGroupsCurrentlyNotFetching((filter) => async (dispatch, getState) => {
  const state = getState();
  const key = keyifyParamsForFiltersRequest(state);
  const baseParams = paramsForFilterGroups(state);
  const id = filter.get('id');
  const currentGroups = filter.get('groups');
  const first = currentGroups.count() + 1;
  const last = first + config.COUNT_OF_ITEMS;
  const properties = [
    {
      property: id,
      first,
      last,
    },
  ];

  const resultParams = {
    ...baseParams,
    properties,
  };

  const { groups, groupCount } = await getNormalizedFilterGroupsAsync(resultParams);
  const action = addFilterGroupsById(key, id, groups, groupCount);

  dispatch(action);
});

export const fetchSuppliersPreferencesGroups = (filter) => async (dispatch, getState) => {
  await dispatch(fetchFilterGroups(filter));
  await dispatch(updateFilterGroups(filter));

  const commercialSuppliersFilter = commercialSuppliersFilterSelector(getState());
  if (commercialSuppliersFilter) {
    await dispatch(fetchFilterGroups(commercialSuppliersFilter, true));
  }
};
